import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Box, Button, ButtonBase, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonPublicGallery from '../../salonPublicAppearance/SalonPublicGallery';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const GALLERY_VARIANTS = new Set([
  'saloone',
  'editorial',
  'mosaic',
  'strip',
  'secondary',
]);

const SalonTemplateGallery = ({
  data = [],
  variant = 'saloone',
  visible = true,
  mode = 'public',
  onSelectPhoto,
  onNavigatePhoto,
  selectedIndex = 0,
  salonId,
  title,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const photos = Array.isArray(data) ? data.filter((photo) => photo?.src) : [];
  const galleryVariant = GALLERY_VARIANTS.has(variant) ? variant : 'saloone';
  const preview = mode === 'preview';
  const hasSalonId = salonId !== null
    && salonId !== undefined
    && String(salonId).trim() !== '';

  if (!visible || (!preview && photos.length === 0)) return null;

  if (photos.length === 0) {
    return (
      <SalonPublicStyleScope
        sectionKey="gallery"
        data-testid="salon-section-gallery"
        data-salon-template-block="gallery"
        data-variant={galleryVariant}
        sx={{ mt: 4, '&:first-of-type': { mt: 0 } }}
      >
        {title ? <Box data-salon-text-role="title">{title}</Box> : null}
        <Box data-salon-text-role="content">
          <Box
            data-testid="salon-gallery-empty-state"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: { xs: 260, sm: 300 },
              px: { xs: 2.5, sm: 4 },
              py: { xs: 4, sm: 5 },
              textAlign: 'center',
              color: theme.salon.onBackground,
              backgroundColor: theme.salon.surfaceMuted,
              border: `1px dashed ${theme.salon.primary}`,
              borderRadius: 3,
            }}
          >
            <ImageOutlinedIcon aria-hidden="true" sx={{ fontSize: 44, color: theme.salon.primary }} />
            <Typography component="h3" variant="h6" sx={{ color: 'inherit', fontWeight: 700 }}>
              {t('salon_theme.preview.gallery_empty.title')}
            </Typography>
            <Typography sx={{ maxWidth: 520, color: 'inherit' }}>
              {t('salon_theme.preview.gallery_empty.body')}
            </Typography>
            {hasSalonId ? (
              <Button
                component="a"
                data-testid="salon-gallery-manage-photos"
                data-salon-preview-interactive="true"
                href={`/pro/salon/${salonId}?editPhotos=true`}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  minHeight: 44,
                  mt: 1,
                  px: 3,
                  color: theme.salon.onPrimary,
                  backgroundColor: theme.salon.primary,
                  '&:hover': {
                    color: theme.salon.onPrimary,
                    backgroundColor: theme.salon.primary,
                  },
                }}
              >
                {t('salon_theme.preview.gallery_empty.action')}
              </Button>
            ) : null}
          </Box>
        </Box>
      </SalonPublicStyleScope>
    );
  }

  const photoCount = photos.length;
  const currentIndex = Number.isInteger(selectedIndex)
    ? ((selectedIndex % photoCount) + photoCount) % photoCount
    : 0;
  const displayPhotos = photos.slice(0, 5);
  const remainingCount = Math.max(0, photoCount - 5);
  const gridLayout = photoCount === 1
    ? { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' }
    : photoCount === 2
      ? { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' }
      : photoCount === 3
        ? { gridTemplateColumns: '2fr 1fr', gridTemplateRows: '1fr 1fr' }
        : { gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: '1fr 1fr' };

  const gallery = galleryVariant === 'saloone' ? (
    <>
      <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 4 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '4 / 3',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <ButtonBase
            aria-label={t('landing_salon_details.view_all_photos', { count: photoCount })}
            aria-disabled={String(preview)}
            disabled={preview}
            onClick={() => onSelectPhoto?.(currentIndex)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${photos[currentIndex].src})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </ButtonBase>
          {photoCount > 1 ? (
            <>
              <IconButton
                aria-label={t('actions.previous')}
                aria-disabled={String(preview)}
                data-testid="salon-gallery-mobile-previous"
                disabled={preview}
                onClick={() => onNavigatePhoto?.((currentIndex - 1 + photoCount) % photoCount)}
                sx={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'white', bgcolor: 'rgba(0,0,0,0.35)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
              </IconButton>
              <IconButton
                aria-label={t('actions.next')}
                aria-disabled={String(preview)}
                data-testid="salon-gallery-mobile-next"
                disabled={preview}
                onClick={() => onNavigatePhoto?.((currentIndex + 1) % photoCount)}
                sx={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'white', bgcolor: 'rgba(0,0,0,0.35)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: '2rem' }} />
              </IconButton>
            </>
          ) : null}
        </Box>
      </Box>

      <Box
        data-testid="salon-public-gallery"
        data-compact="false"
        sx={{
          display: { xs: 'none', sm: 'grid' },
          ...gridLayout,
          gap: 1,
          height: 400,
          [HIGH_DENSITY_TABLET_MEDIA]: { height: 'clamp(480px, 34vw, 620px)' },
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        {displayPhotos.map((photo, index) => (
          <ButtonBase
            key={photo.id ?? `${photo.src}-${index}`}
            aria-label={photo.alt || `Photo ${index + 1}`}
            aria-disabled={String(preview)}
            disabled={preview}
            onClick={() => onSelectPhoto?.(index)}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              gridRow: index === 0 && photoCount >= 3 ? '1 / 3' : 'auto',
              gridColumn: index === 0 && photoCount >= 3 ? '1 / 2' : 'auto',
              bgcolor: index === 0 ? theme.colors.gray[100] : 'transparent',
              '&:hover img': { transform: 'scale(1.05)' },
              '&:hover .photo-overlay': { opacity: index === 4 && remainingCount > 0 ? 0.8 : 1 },
            }}
          >
            <Box
              component="img"
              data-testid="salon-public-gallery-photo"
              src={photo.src}
              alt={photo.alt || ''}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: index === 0 ? 'contain' : 'cover',
                transition: 'transform 0.3s ease',
              }}
            />
            <Box
              className="photo-overlay"
              sx={{
                position: 'absolute', inset: 0,
                display: 'grid', placeItems: 'center',
                bgcolor: index === 4 && remainingCount > 0 ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.3)',
                opacity: index === 4 && remainingCount > 0 ? 0.6 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              {index === 4 && remainingCount > 0 ? (
                <Typography sx={{ color: 'white', fontWeight: 600 }}>
                  {t('landing_salon_details.view_all_photos', { count: photoCount })}
                </Typography>
              ) : null}
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </>
  ) : (
    <SalonPublicGallery photos={photos} compact={galleryVariant === 'strip'} />
  );

  return (
    <SalonPublicStyleScope
      sectionKey="gallery"
      data-testid="salon-section-gallery"
      data-salon-template-block="gallery"
      data-variant={galleryVariant}
      sx={{ mt: 4, '&:first-of-type': { mt: 0 } }}
    >
      {title ? <Box data-salon-text-role="title">{title}</Box> : null}
      <Box data-salon-text-role="content">{gallery}</Box>
    </SalonPublicStyleScope>
  );
};

export default SalonTemplateGallery;
